import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { askedQuestionSchema } from '@inference.engine/agentic/human-input';
import { getChatHistory } from '@/features/chats/api/chats.api';
import type { AgentMessage, Message, UserMessage } from '@/features/chats/model/chats.types';
import {
  getAgentMetadataHelpers,
  getPersonaIdFromAgentId,
  SPECIAL_AGENT_IDS,
} from '@/entities/agent';
import { parseAgentMessageContent } from '@/features/chats/lib/parseAgentMessageContent';
import { annotateHistoricalMessages } from '@/entities/ai';
import { logger } from '@/lib/utils/logger';
import { CHAT_HISTORY_QUERY_KEY_ROOT } from '@/shared/constants/query-keys.constants';
import { resolveArtifactTool } from '@/shared/constants/widget-tool-mapping';
import type { ProcessedChatHistory, RawChatHistory } from './useChatHistory.types';

/**
 * The agent a chat resumes with: the server's record, or, when it names none, whoever the last
 * message involved (left null, the caller would fall back to the agent of the chat open
 * before). One exception: an Audience chat the server left on the router after a switch to
 * another persona was cancelled. The cancelled request is not stored, so the persona that
 * last replied is still the one the chat is with.
 */
function resolveLastAgentId(activeAgent: string | null | undefined, messages: Message[]) {
  const lastReplyAgentId = messages.findLast((message) => message.type === 'agent')?.agentId;
  if (
    activeAgent === SPECIAL_AGENT_IDS.AUDIENCE_FLOW &&
    getPersonaIdFromAgentId(lastReplyAgentId)
  ) {
    return lastReplyAgentId ?? null;
  }
  return activeAgent ?? messages.findLast((message) => message.agentId)?.agentId ?? null;
}

async function fetchAndProcessChatHistory(contextId: string): Promise<RawChatHistory> {
  const history = await getChatHistory({
    contextId,
    includeActiveAgent: true,
    take: 1000,
  });

  const widgets: ProcessedChatHistory['widgets'] = [];

  const messages = history.exchanges.reduce<Message[]>((messages, exchange) => {
    if (!exchange.data) {
      logger.warn('Skipping exchange with no data:', exchange);
      return messages;
    }

    switch (exchange.type) {
      case 'request': {
        const { prompt, ...inputs } = exchange.data;
        const agentId = exchange.recipient.id;

        messages.push({
          type: 'user',
          messageId: exchange.id,
          content: prompt as UserMessage['content'],
          agentId,
          files: exchange.files as UserMessage['files'],
          metadata: exchange.metadata as UserMessage['metadata'],
          inputs: (inputs as UserMessage['inputs']) ?? undefined,
        } satisfies UserMessage);

        break;
      }
      case 'reply': {
        const messageId = exchange.id;
        const agentId = exchange.sender.id;

        messages.push({
          type: 'agent',
          messageId,
          content: parseAgentMessageContent(exchange.data),
          ...askedQuestionsOf(exchange.data),
          agentId,
          files: exchange.files ?? [],
          status: 'complete' as const,
          // eslint-disable-next-line i18next/no-literal-string -- dead field: `AgentMessage.subtext` is written here and read by no component
          subtext: 'Complete',
        } satisfies AgentMessage);

        const metadata = getAgentMetadataHelpers();
        const senderHasView = metadata.resolveAgentMetadata(agentId) !== undefined;
        // Mirrors the live stream: the agent that produced the widget owns its view when Studio
        // knows that agent. A version with no agent creator (a user's saved edit, or a row from a
        // server that did not report creators) falls back to the agent the widget's key belongs
        // to, and only when the sender has no view of its own.
        const knownProducer = (id: string | undefined) =>
          id && id !== agentId && metadata.resolveAgentMetadata(id) ? id : undefined;
        const producerOf = (artifact: {
          key: string;
          creatorType?: string;
          creatorId?: string;
        }) => {
          if (artifact.creatorType === 'agent' && artifact.creatorId) {
            return knownProducer(artifact.creatorId);
          }
          return senderHasView
            ? undefined
            : knownProducer(resolveArtifactTool(artifact.key)?.agentName);
        };
        exchange.artifacts
          ?.filter((artifact) => artifact.isWidget)
          .sort((a, b) => a.version - b.version)
          .forEach((artifact) => {
            const sourceAgentId = producerOf(artifact);
            const agentTitle = metadata.getAgentTitle(sourceAgentId ?? agentId);
            widgets.push({
              messageId,
              agentId,
              widget: {
                key: artifact.key,
                agentName: agentTitle,
                ...(sourceAgentId ? { sourceAgentId } : {}),
                data: artifact.data,
                version: artifact.version,
                exchangeId: exchange.id,
                artifactId: artifact.id,
                // Real per-version creation time — the widget store prefers this over Date.now()
                // so historical versions show their true time in the version dropdown.
                createdAt: artifact.createdAt,
              },
              agentTitle,
            });
          });

        break;
      }
    }

    return annotateHistoricalMessages(messages);
  }, []);

  return {
    messages,
    lastAgentId: resolveLastAgentId(history.activeAgent, messages),
    widgets,
  };
}

interface UseChatHistoryOptions {
  contextId: string;
  defaultAgentId: string;
  enabled?: boolean;
  onSuccess?: (data: ProcessedChatHistory) => void;
}

export const useChatHistory = ({
  contextId,
  defaultAgentId,
  enabled = true,
  onSuccess,
}: UseChatHistoryOptions) => {
  // Store latest callback in a ref to avoid re-triggering effect. Kept current: the chat page
  // stays mounted from one chat to the next, and the first render's callback would hand every
  // later chat's history to the chat the page opened on.
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const query = useQuery({
    queryKey: [CHAT_HISTORY_QUERY_KEY_ROOT, contextId],
    queryFn: () => fetchAndProcessChatHistory(contextId),
    select: (data): ProcessedChatHistory => {
      return {
        ...data,
        lastAgentId: data.lastAgentId ?? defaultAgentId,
      };
    },
    enabled: enabled && !!contextId,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const { data } = query;

  // Keyed on the chat as well as its data: structural sharing hands a cached chat whose history
  // deep-equals the one just left (two empty chats, say) the previous reference, and on `data`
  // alone the new chat's history would never be delivered. Not on the callback, which the ref
  // keeps current.
  useEffect(() => {
    if (data) {
      onSuccessRef.current?.(data);
    }
  }, [contextId, data]);

  return query;
};

// Where a question was asked only places it, so a bad offset reads as unplaced rather than
// hiding every question on the reply.
const storedAskedQuestionSchema = askedQuestionSchema.extend({
  at: askedQuestionSchema.shape.at.catch(undefined),
  callId: askedQuestionSchema.shape.callId.catch(undefined),
});

const askedQuestionsSchema = z.object({
  askedQuestions: z.array(storedAskedQuestionSchema).min(1),
});

/** The questions a stored reply asked and what the person answered, if they did. */
function askedQuestionsOf(data: unknown): Pick<AgentMessage, 'askedQuestions'> {
  const parsed = askedQuestionsSchema.safeParse(data);
  return parsed.success ? { askedQuestions: parsed.data.askedQuestions } : {};
}
